import axios from 'axios';
import { initAxios } from '../utils/axiosHelper';

export type DocumentLibraryOrigin = 'document_upload' | 'report_builder';
export type DocumentLibraryReferenceType =
  | 'ace_run'
  | 'tariff_database'
  | 'product_database';

export interface DocumentLibraryReference {
  id: string;
  role: 'input' | 'output';
  referenceType: DocumentLibraryReferenceType;
  referenceId: string;
  referenceName: string;
}

export interface DocumentLibraryFile {
  id: string;
  role: 'input' | 'output';
  status: 'pending' | 'ready' | 'failed';
  fileName: string;
  contentType?: string | null;
  fileSizeBytes?: number | null;
}

export interface DocumentLibraryEntry {
  id: string;
  origin: DocumentLibraryOrigin;
  status: 'processing' | 'completed' | 'failed';
  title: string;
  createdBy: string;
  createdByName?: string | null;
  createdAt: string;
  completedAt?: string | null;
  errorMessage?: string | null;
  references: DocumentLibraryReference[];
  files: DocumentLibraryFile[];
}

type RawEntry = Record<string, any>;

const normalizeEntry = (entry: RawEntry): DocumentLibraryEntry => ({
  id: entry.id,
  origin: entry.origin,
  status: entry.status,
  title: entry.title,
  createdBy: entry.created_by,
  createdByName: entry.created_by_name,
  createdAt: entry.created_at,
  completedAt: entry.completed_at,
  errorMessage: entry.error_message,
  references: (entry.references || []).map((reference: RawEntry) => ({
    id: reference.id,
    role: reference.role,
    referenceType: reference.reference_type,
    referenceId: reference.reference_id,
    referenceName: reference.reference_name,
  })),
  files: (entry.files || []).map((file: RawEntry) => ({
    id: file.id,
    role: file.role,
    status: file.status,
    fileName: file.file_name,
    contentType: file.content_type,
    fileSizeBytes:
      file.file_size_bytes === null || file.file_size_bytes === undefined
        ? null
        : Number(file.file_size_bytes),
  })),
});

const headersForTeam = (teamId: string) => ({
  'X-Selected-Team-Id': teamId,
});

export const DocumentLibraryAPI = {
  list: async (
    teamId: string,
    options: {
      origin?: DocumentLibraryOrigin;
      query?: string;
      page?: number;
      limit?: number;
    } = {},
  ): Promise<{ data: DocumentLibraryEntry[]; total: number }> => {
    const client = await initAxios();
    const response = await client.get('/document-library', {
      headers: headersForTeam(teamId),
      params: {
        origin: options.origin,
        q: options.query || undefined,
        page: options.page || 1,
        limit: options.limit || 50,
      },
    });
    return {
      data: (response.data?.data || []).map(normalizeEntry),
      total: Number(response.data?.total || 0),
    };
  },

  syncDocumentBatch: async (
    batchId: string,
    teamId: string,
  ): Promise<DocumentLibraryEntry> => {
    const client = await initAxios();
    const response = await client.post(
      `/document-library/document-batches/${encodeURIComponent(batchId)}/sync`,
      undefined,
      { headers: headersForTeam(teamId) },
    );
    return normalizeEntry(response.data.data);
  },

  createReportEntry: async (
    teamId: string,
    input: {
      clientRequestId: string;
      title: string;
      sources: Array<{ type: 'ace' | 'tariff' | 'product'; id: string }>;
      metadata?: Record<string, unknown>;
    },
  ): Promise<DocumentLibraryEntry> => {
    const client = await initAxios();
    const response = await client.post('/document-library/report-entries', input, {
      headers: headersForTeam(teamId),
    });
    return normalizeEntry(response.data.data);
  },

  uploadReportOutput: async (
    teamId: string,
    entryId: string,
    fileName: string,
    blob: Blob,
  ): Promise<void> => {
    const client = await initAxios();
    const presign = await client.post(
      `/document-library/entries/${entryId}/files/presign`,
      {
        fileName: fileName,
        contentType: 'text/csv',
        fileSizeBytes: blob.size,
      },
      { headers: headersForTeam(teamId) },
    );
    const { fileId, uploadUrl } = presign.data.data;
    await axios.put(uploadUrl, blob, {
      headers: { 'Content-Type': 'text/csv' },
      timeout: 0,
    });
    await client.post(
      `/document-library/files/${fileId}/complete`,
      { expectedSizeBytes: blob.size },
      { headers: headersForTeam(teamId) },
    );
  },

  getDownload: async (
    teamId: string,
    fileId: string,
  ): Promise<{ url: string; fileName: string }> => {
    const client = await initAxios();
    const response = await client.get(
      `/document-library/files/${fileId}/download`,
      { headers: headersForTeam(teamId) },
    );
    return response.data.data;
  },
};

export default DocumentLibraryAPI;

